import { useMemo, useState } from "react";
import { format } from "date-fns";
import { Handshake } from "lucide-react";
import { Badge } from "../../../../../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../../../components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../../../components/ui/tooltip";
import EmptyTabState from "../../../../../shared/ui/EmptyTabState";
import ObjectDetailSheet from "./ObjectDetailSheet";

type Props = {
  records: any[];
  searchQuery?: string;
  stageFilter?: string;
};

const STAGE_CLASSES: Record<string, string> = {
  "closed won": "bg-emerald-50 text-emerald-700 border-emerald-200",
  won: "bg-emerald-50 text-emerald-700 border-emerald-200",
  "closed lost": "bg-red-50 text-red-700 border-red-200",
  lost: "bg-red-50 text-red-700 border-red-200",
  closed: "bg-muted text-muted-foreground",
  negotiation: "bg-orange-50 text-orange-700 border-orange-200",
  proposal: "bg-indigo-50 text-indigo-700 border-indigo-200",
  qualification: "bg-blue-50 text-blue-700 border-blue-200",
  discovery: "bg-cyan-50 text-cyan-700 border-cyan-200",
  demo: "bg-violet-50 text-violet-700 border-violet-200",
};

function getStageClass(stage: string): string {
  if (!stage) return "";
  const lower = stage.toLowerCase();
  for (const [key, cls] of Object.entries(STAGE_CLASSES)) {
    if (lower.includes(key)) return cls;
  }
  return "bg-blue-50 text-blue-700 border-blue-200";
}

function formatCurrency(amount: any, currency?: string): string {
  if (amount === null || amount === undefined || amount === "") return "--";
  const num = typeof amount === "string" ? parseFloat(amount) : amount;
  if (Number.isNaN(num)) return "--";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 0,
    }).format(num);
  } catch {
    return `$${num.toLocaleString()}`;
  }
}

function safeFormat(value: any, fmt: string): string {
  if (!value) return "--";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "--";
    return format(d, fmt);
  } catch {
    return "--";
  }
}

function getField(record: any, ...keys: string[]): string {
  for (const k of keys) {
    if (record[k]) return record[k];
  }
  return "";
}

function matchesSearch(record: any, query: string): boolean {
  if (!query.trim()) return true;
  const q = query.toLowerCase();
  const haystack = [
    record.title, record.native_title,
    record.stage, record.native_stage,
    record.pipeline, record.native_pipeline,
    record.owner_email, record.native_owner_email,
  ].filter(Boolean).join(" ").toLowerCase();
  return haystack.includes(q);
}

const DealsView = ({ records, searchQuery = "", stageFilter = "" }: Props) => {
  const [selected, setSelected] = useState<any>(null);

  const filtered = useMemo(() => {
    let result = records;
    if (searchQuery.trim()) {
      result = result.filter((r) => matchesSearch(r, searchQuery));
    }
    if (stageFilter) {
      result = result.filter((r) => {
        const stage = (r.stage || r.native_stage || "").toLowerCase();
        return stage === stageFilter.toLowerCase();
      });
    }
    return result;
  }, [records, searchQuery, stageFilter]);

  if (filtered.length === 0) {
    return (
      <EmptyTabState
        title="No deals yet"
        description={searchQuery || stageFilter ? "Try adjusting your filters." : "Deals associated with this account will appear here."}
        icon={<Handshake size={24} />}
      />
    );
  }

  return (
    <TooltipProvider>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Deal</TableHead>
            <TableHead>Stage</TableHead>
            <TableHead className="text-right">Amount</TableHead>
            <TableHead>Close Date</TableHead>
            <TableHead>Pipeline</TableHead>
            <TableHead>Owner</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filtered.map((record: any) => {
            const id = record.id || record.external_id || record.native_external_id;
            const title = getField(record, "title", "native_title") || "(Untitled deal)";
            const stage = getField(record, "stage", "native_stage");
            const amount = record.amount || record.native_amount;
            const currency = getField(record, "currency", "native_currency");
            const closeDate = record.close_date_tz || record.native_close_date_tz;
            const pipeline = getField(record, "pipeline", "native_pipeline");
            const ownerEmail = getField(record, "owner_email", "native_owner_email");

            return (
              <TableRow
                key={id || Math.random()}
                className="cursor-pointer"
                onClick={() => setSelected(record)}
              >
                <TableCell>
                  <span className="text-[13px] font-medium text-foreground">{title}</span>
                </TableCell>
                <TableCell>
                  {stage ? (
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border ${getStageClass(stage)}`}>
                      {stage}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">--</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <span className="text-[13px] font-medium text-foreground">
                    {formatCurrency(amount, currency)}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="text-[13px] text-muted-foreground">
                    {safeFormat(closeDate, "MMM d, yyyy")}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="text-[13px] text-muted-foreground">{pipeline || "--"}</span>
                </TableCell>
                <TableCell>
                  {ownerEmail ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="text-[13px] text-muted-foreground truncate block max-w-[150px]">
                          {ownerEmail}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>{ownerEmail}</TooltipContent>
                    </Tooltip>
                  ) : (
                    <span className="text-muted-foreground">--</span>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {selected && (
        <ObjectDetailSheet
          open={!!selected}
          onClose={() => setSelected(null)}
          title={getField(selected, "title", "native_title") || "(Untitled deal)"}
          badge={
            getField(selected, "stage", "native_stage")
              ? { label: getField(selected, "stage", "native_stage") }
              : undefined
          }
          sourceUrl={getField(selected, "source_url", "native_source_url")}
          fields={[
            { label: "Stage", value: getField(selected, "stage", "native_stage"), type: "badge" },
            { label: "Amount", value: formatCurrency(selected.amount || selected.native_amount, getField(selected, "currency", "native_currency")) },
            { label: "Pipeline", value: getField(selected, "pipeline", "native_pipeline") },
            { label: "Close Date", value: selected.close_date_tz || selected.native_close_date_tz, type: "date" },
            { label: "Owner", value: getField(selected, "owner_email", "native_owner_email") },
            { label: "Source", value: getField(selected, "source", "native_source") },
            { label: "Created", value: selected.created_at_tz || selected.native_created_at_tz || selected.created_at, type: "date" },
          ]}
          bodyMarkdown={getField(selected, "deal_summary_md", "native_deal_summary_md")}
          bodyLabel="Deal Summary"
        />
      )}
    </TooltipProvider>
  );
};

export default DealsView;
